import styled from '@emotion/styled'
import * as OutlineIcons from '@heroicons/react/outline'
import * as SolidIcons from '@heroicons/react/solid'

import {
  BemloIcons,
  CustomIconName,
  customOutlineIcons,
  customSolidIcons,
} from './icons'
import { theme } from '../../constants'
import { Color } from '../../constants/color'
import { StyleProps, createStyles } from '../../utils/create-styles'
import { SpaceProps, space } from '../../utils/styled-system'

export type IconSize = keyof (typeof theme)['elements']['icon']['size']

const IconElement = styled.div<IconProps>((props) => {
  const width =
    typeof props.size === 'number'
      ? props.size
      : theme.elements.icon.size[props.size ?? 'md']

  return {
    backgroundColor:
      props.backgroundColor ?? (props.round ? Color.LIGHT_GREY : undefined),
    borderRadius: 999,
    boxSizing: 'border-box',
    color: props.color,
    cursor: props.onClick ? 'pointer' : undefined,
    display: 'flex',
    flexShrink: 0,
    opacity: props.opacity,
    padding: props.round ? width / 8 : undefined,
    width,
    ...space(props),
    ...createStyles(props.styles),
  }
})

const StyledSvg = styled.svg<{ flipped: boolean | undefined }>(
  ({ flipped }) => ({
    width: '100%',
    transform: flipped ? 'scaleX(-1)' : undefined,
  }),
)

export type IconDesign = 'outline' | 'solid'

type HeroIconName = keyof (typeof OutlineIcons & typeof SolidIcons)
type BemloIconName = keyof typeof BemloIcons

export type IconName = HeroIconName | BemloIconName | CustomIconName

export type IconProps = SpaceProps & {
  backgroundColor?: Color
  className?: string
  color?: Color | string
  design?: IconDesign
  name: IconName
  onClick?: () => void
  opacity?: number
  round?: boolean
  size?: IconSize | number
  styles?: StyleProps
  title?: string
  flippedHorizontal?: boolean
}

const getIconComponent = (name: IconName, design: IconDesign) => {
  if (name in BemloIcons) {
    return BemloIcons[name as BemloIconName]
  }

  if (name in customSolidIcons) {
    return design === 'solid'
      ? // eslint-disable-next-line import/namespace
        customSolidIcons[name as CustomIconName]
      : // eslint-disable-next-line import/namespace
        customOutlineIcons[name as CustomIconName]
  }

  return design === 'solid'
    ? // eslint-disable-next-line import/namespace
      SolidIcons[name as HeroIconName]
    : // eslint-disable-next-line import/namespace
      OutlineIcons[name as HeroIconName]
}

export const Icon = (props: IconProps) => {
  const Component = getIconComponent(props.name, props.design ?? 'outline')

  return (
    <IconElement {...props}>
      <StyledSvg as={Component} flipped={props.flippedHorizontal} />
    </IconElement>
  )
}
